import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

import DefaultLayout from "/home/runner/work/documentation/documentation/node_modules/gatsby-theme-doctornpm/src/components/layout.js";
export const _frontmatter = {};
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <h3>{`Description`}</h3>
    <p>{`npm puts various things on your computer.  That's its job.`}</p>
    <p>{`This document will tell you what it puts where.`}</p>
    <h4>{`tl;dr`}</h4>
    <ul>
      <li parentName="ul">{`Local install (default): puts stuff in `}<inlineCode parentName="li">{`./node_modules`}</inlineCode>{` of the current
package root.`}</li>
      <li parentName="ul">{`Global install (with `}<inlineCode parentName="li">{`-g`}</inlineCode>{`): puts stuff in /usr/local or wherever node
is installed.`}</li>
      <li parentName="ul">{`Install it `}<strong parentName="li">{`locally`}</strong>{` if you're going to `}<inlineCode parentName="li">{`require()`}</inlineCode>{` it.`}</li>
      <li parentName="ul">{`Install it `}<strong parentName="li">{`globally`}</strong>{` if you're going to run it on the command line.`}</li>
      <li parentName="ul">{`If you need both, then install it in both places, or use `}<inlineCode parentName="li">{`npm link`}</inlineCode>{`.`}</li>
    </ul>
    <h4>{`prefix Configuration`}</h4>
    <p>{`The `}<inlineCode parentName="p">{`prefix`}</inlineCode>{` config defaults to the location where node is installed.
On most systems, this is `}<inlineCode parentName="p">{`/usr/local`}</inlineCode>{`. On Windows, it's `}<inlineCode parentName="p">{`%AppData%\\npm`}</inlineCode>{`.
On Unix systems, it's one level up, since node is typically installed at
`}<inlineCode parentName="p">{`{prefix}/bin/node`}</inlineCode>{` rather than `}<inlineCode parentName="p">{`{prefix}/node.exe`}</inlineCode>{`.`}</p>
    <p>{`When the `}<inlineCode parentName="p">{`global`}</inlineCode>{` flag is set, npm installs things into this prefix.
When it is not set, it uses the root of the current package, or the
current working directory if not in a package already.`}</p>
    <h4>{`Node Modules`}</h4>
    <p>{`Packages are dropped into the `}<inlineCode parentName="p">{`node_modules`}</inlineCode>{` folder under the `}<inlineCode parentName="p">{`prefix`}</inlineCode>{`.
When installing locally, this means that you can
`}<inlineCode parentName="p">{`require("packagename")`}</inlineCode>{` to load its main module, or
`}<inlineCode parentName="p">{`require("packagename/lib/path/to/sub/module")`}</inlineCode>{` to load other modules.`}</p>
    <p>{`Global installs on Unix systems go to `}<inlineCode parentName="p">{`{prefix}/lib/node_modules`}</inlineCode>{`.
Global installs on Windows go to `}<inlineCode parentName="p">{`{prefix}/node_modules`}</inlineCode>{` (that is, no
`}<inlineCode parentName="p">{`lib`}</inlineCode>{` folder.)`}</p>
    <p>{`Scoped packages are installed the same way, except they are grouped together
in a sub-folder of the relevant `}<inlineCode parentName="p">{`node_modules`}</inlineCode>{` folder with the name of that
scope prefix by the @ symbol, e.g. `}<inlineCode parentName="p">{`npm install @myorg/package`}</inlineCode>{` would place
the package in `}<inlineCode parentName="p">{`{prefix}/node_modules/@myorg/package`}</inlineCode>{`. See
`}<a parentName="p" {...{
        "href": "/cli/v8/using-npm/scope"
      }}><inlineCode parentName="a">{`scope`}</inlineCode></a>{` for more details.`}</p>
    <p>{`If you wish to `}<inlineCode parentName="p">{`require()`}</inlineCode>{` a package, then install it locally.`}</p>
    <h4>{`Executables`}</h4>
    <p>{`When in global mode, executables are linked into `}<inlineCode parentName="p">{`{prefix}/bin`}</inlineCode>{` on Unix,
or directly into `}<inlineCode parentName="p">{`{prefix}`}</inlineCode>{` on Windows.  Ensure that path is in your
terminal's `}<inlineCode parentName="p">{`PATH`}</inlineCode>{` environment to run them.`}</p>
    <p>{`When in local mode, executables are linked into
`}<inlineCode parentName="p">{`./node_modules/.bin`}</inlineCode>{` so that they can be made available to scripts run
through npm.  (For example, so that a test runner will be in the path
when you run `}<inlineCode parentName="p">{`npm test`}</inlineCode>{`.)`}</p>
    <h4>{`Man Pages`}</h4>
    <p>{`When in global mode, man pages are linked into `}<inlineCode parentName="p">{`{prefix}/share/man`}</inlineCode>{`.`}</p>
    <p>{`When in local mode, man pages are not installed.`}</p>
    <p>{`Man pages are not installed on Windows systems.`}</p>
    <h4>{`Cache`}</h4>
    <p>{`See `}<a parentName="p" {...{
        "href": "/cli/v8/commands/npm-cache"
      }}><inlineCode parentName="a">{`npm cache`}</inlineCode></a>{`.  Cache files are stored in `}<inlineCode parentName="p">{`~/.npm`}</inlineCode>{` on Posix, or
`}<inlineCode parentName="p">{`%AppData%/npm-cache`}</inlineCode>{` on Windows.`}</p>
    <p>{`This is controlled by the `}<inlineCode parentName="p">{`cache`}</inlineCode>{` configuration param.`}</p>
    <h4>{`Temp Files`}</h4>
    <p>{`Temporary files are stored by default in the folder specified by the
`}<inlineCode parentName="p">{`tmp`}</inlineCode>{` config, which defaults to the TMPDIR, TMP, or TEMP environment
variables, or `}<inlineCode parentName="p">{`/tmp`}</inlineCode>{` on Unix and `}<inlineCode parentName="p">{`c:\\windows\\temp`}</inlineCode>{` on Windows.`}</p>
    <p>{`Temp files are given a unique folder under this root for each run of the
program, and are deleted upon successful exit.`}</p>
    <h3>{`More Information`}</h3>
    <p>{`When installing locally, npm first tries to find an appropriate
`}<inlineCode parentName="p">{`prefix`}</inlineCode>{` folder.  This is so that `}<inlineCode parentName="p">{`npm install foo@1.2.3`}</inlineCode>{` will install
to the sensible root of your package, even if you happen to have `}<inlineCode parentName="p">{`cd`}</inlineCode>{`ed
into some other folder.`}</p>
    <p>{`Starting at the $PWD, npm will walk up the folder tree checking for a
folder that contains either a `}<inlineCode parentName="p">{`package.json`}</inlineCode>{` file, or a `}<inlineCode parentName="p">{`node_modules`}</inlineCode>{`
folder.  If such a thing is found, then that is treated as the effective
"current directory" for the purpose of running npm commands.  (This
behavior is inspired by and similar to git's .git-folder seeking
logic when running git commands in a working dir.)`}</p>
    <p>{`If no package root is found, then the current folder is used.`}</p>
    <p>{`When you run `}<inlineCode parentName="p">{`npm install foo@1.2.3`}</inlineCode>{`, then the package is loaded into
the cache, and then unpacked into `}<inlineCode parentName="p">{`./node_modules/foo`}</inlineCode>{`.  Then, any of
foo's dependencies are similarly unpacked into
`}<inlineCode parentName="p">{`./node_modules/foo/node_modules/...`}</inlineCode>{`.`}</p>
    <p>{`Any bin files are symlinked to `}<inlineCode parentName="p">{`./node_modules/.bin/`}</inlineCode>{`, so that they may
be found by npm scripts when necessary.`}</p>
    <h4>{`Global Installation`}</h4>
    <p>{`If the `}<inlineCode parentName="p">{`global`}</inlineCode>{` configuration is set to true, then npm will
install packages "globally".`}</p>
    <p>{`For global installation, packages are installed roughly the same way,
but using the folders described above.`}</p>
    <h4>{`Cycles, Conflicts, and Folder Parsimony`}</h4>
    <p>{`Cycles are handled using the property of node's module system that it
walks up the directories looking for `}<inlineCode parentName="p">{`node_modules`}</inlineCode>{` folders.  So, at every
stage, if a package is already installed in an ancestor `}<inlineCode parentName="p">{`node_modules`}</inlineCode>{`
folder, then it is not installed at the current location.`}</p>
    <p>{`Consider the case above, where `}<inlineCode parentName="p">{`foo -> bar -> baz`}</inlineCode>{`.  Imagine if, in
addition to that, baz depended on bar, so you'd have:
`}<inlineCode parentName="p">{`foo -> bar -> baz -> bar -> baz ...`}</inlineCode>{`.  However, since the folder
structure is: `}<inlineCode parentName="p">{`foo/node_modules/bar/node_modules/baz`}</inlineCode>{`, there's no need to
put another copy of bar into `}<inlineCode parentName="p">{`.../baz/node_modules`}</inlineCode>{`, since when it calls
require("bar"), it will get the copy that is installed in
`}<inlineCode parentName="p">{`foo/node_modules/bar`}</inlineCode>{`.`}</p>
    <p>{`This shortcut is only used if the exact same
version would be installed in multiple nested `}<inlineCode parentName="p">{`node_modules`}</inlineCode>{` folders.  It
is still possible to have `}<inlineCode parentName="p">{`a/node_modules/b/node_modules/a`}</inlineCode>{` if the two
"a" packages are different versions.  However, without repeating the
exact same package multiple times, an infinite regress will always be
prevented.`}</p>
    <p>{`Another optimization can be made by installing dependencies at the
highest level possible, below the localized "target" folder.`}</p>
    <h4>{`Example`}</h4>
    <p>{`Consider this dependency graph:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-bash"
      }}>{`foo
+-- blerg@1.2.5
+-- bar@1.2.3
|   +-- blerg@1.x (latest=1.3.7)
|   +-- baz@2.x
|   |   \`-- quux@3.x
|   |       \`-- bar@1.2.3 (cycle)
|   \`-- asdf@*
\`-- baz@1.2.3
    \`-- quux@3.x
        \`-- bar
`}</code></pre>
    <p>{`In this case, we might expect a folder structure like this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-bash"
      }}>{`foo
+-- node_modules
    +-- blerg (1.2.5) <---[A]
    +-- bar (1.2.3) <---[B]
    |   \`-- node_modules
    |       +-- baz (2.0.2) <---[C]
    |       |   \`-- node_modules
    |       |       \`-- quux (3.2.0)
    |       \`-- asdf (2.3.4)
    \`-- baz (1.2.3) <---[D]
        \`-- node_modules
            \`-- quux (3.2.0) <---[E]
`}</code></pre>
    <p>{`Since foo depends directly on `}<inlineCode parentName="p">{`bar@1.2.3`}</inlineCode>{` and `}<inlineCode parentName="p">{`baz@1.2.3`}</inlineCode>{`, those are
installed in foo's `}<inlineCode parentName="p">{`node_modules`}</inlineCode>{` folder.`}</p>
    <p>{`Even though the latest copy of blerg is 1.3.7, foo has a specific
dependency on version 1.2.5.  So, that gets installed at `}{`[A]`}{`.  Since the
parent installation of blerg satisfies bar's dependency on `}<inlineCode parentName="p">{`blerg@1.x`}</inlineCode>{`,
it does not install another copy under `}{`[B]`}{`.`}</p>
    <p>{`Bar `}{`[B]`}{` also has dependencies on baz and asdf, so those are installed in
bar's `}<inlineCode parentName="p">{`node_modules`}</inlineCode>{` folder.  Because it depends on `}<inlineCode parentName="p">{`baz@2.x`}</inlineCode>{`, it cannot
re-use the `}<inlineCode parentName="p">{`baz@1.2.3`}</inlineCode>{` installed in the parent `}<inlineCode parentName="p">{`node_modules`}</inlineCode>{` folder `}{`[D]`}{`,
and must install its own copy `}{`[C]`}{`.`}</p>
    <p>{`Underneath bar, the `}<inlineCode parentName="p">{`baz -> quux -> bar`}</inlineCode>{` dependency creates a cycle.
However, because bar is already in quux's ancestry `}{`[B]`}{`, it does not
unpack another copy of bar into that folder.`}</p>
    <p>{`Underneath `}<inlineCode parentName="p">{`foo -> baz`}</inlineCode>{` `}{`[D]`}{`, quux's `}{`[E]`}{` folder tree is empty, because its
dependency on bar is satisfied by the parent folder copy installed at `}{`[B]`}{`.`}</p>
    <p>{`For a graphical breakdown of what is installed where, use `}<inlineCode parentName="p">{`npm ls`}</inlineCode>{`.`}</p>
    <h4>{`Publishing`}</h4>
    <p>{`Upon publishing, npm will look in the `}<inlineCode parentName="p">{`node_modules`}</inlineCode>{` folder.  If any of
the items there are not in the `}<inlineCode parentName="p">{`bundleDependencies`}</inlineCode>{` array, then they will
not be included in the package tarball.`}</p>
    <p>{`This allows a package maintainer to install all of their dependencies
(and dev dependencies) locally, but only re-publish those items that
cannot be found elsewhere.  See `}<a parentName="p" {...{
        "href": "/cli/v8/configuring-npm/package-json"
      }}><inlineCode parentName="a">{`package.json`}</inlineCode></a>{` for more information.`}</p>
    <h3>{`See also`}</h3>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "/cli/v8/configuring-npm/package-json"
        }}>{`package.json`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "/cli/v8/commands/npm-install"
        }}>{`npm install`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "/cli/v8/commands/npm-pack"
        }}>{`npm pack`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "/cli/v8/commands/npm-cache"
        }}>{`npm cache`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "/cli/v8/commands/npm-config"
        }}>{`npm config`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "/cli/v8/configuring-npm/npmrc"
        }}>{`npmrc`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "/cli/v8/using-npm/config"
        }}>{`config`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "/cli/v8/commands/npm-publish"
        }}>{`npm publish`}</a></li>
    </ul>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      